import React, { useEffect, useState } from 'react';
import { Circle, CircleMarker, Marker, Polygon, Popup, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import helipagSvg from '@/assets/helipad.svg';
import { getFlightNotifications } from '../api/flights.api';
import type { FlightNotification } from '../api/flights.api';
import { flightCategoriesService } from '@/features/notification/services/flightCategories.service';
import { flightsVisibilityService } from '../services/flightsVisibility.service';
import { useObservable } from '@/shared/hooks/useObservable';

const helipagIcon = L.divIcon({
  html: `<img src="${helipagSvg}" alt="helipad" width="24" height="24" style="filter:drop-shadow(0 0 2px #000a)" />`,
  className: '',
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, 0],
  tooltipAnchor: [0, 0],
});

const FLIGHT_COLOR = '#1aff00';   // amber — flight area / takeoff
const BUFFER_COLOR = '#f59e0b';   // yellow — buffer zone
const TERM_COLOR = '#ef4444';     // red — termination zone
const ADDITIONAL_TAKEOFF_COLOR = '#38bdf8'; // blue — additional takeoff points, matches the wizard's map marker

type GeoPolygon = NonNullable<FlightNotification['flight_area']>;

function toPositions(poly: GeoPolygon): [number, number][] {
  return poly.coordinates[0].map(([lng, lat]) => [lat, lng]);
}

function FlightPopup({ flight, extra_text }: { flight: FlightNotification; extra_text?: string }) {
  return (
    <Popup>
      <div style={{ fontSize: 13, lineHeight: 1.5 }}>
        {extra_text && <div style={{ marginTop: 4, color: '#94a3b8' }}>{extra_text}</div>}

        <div style={{ fontWeight: 600 }}>{flight.location_name || `#${flight.id}`}</div>
        <div style={{ color: '#64748b' }}>{flight.flight_date} &nbsp;{flight.time_from}–{flight.time_to}</div>
        {flight.purpose && <div style={{ color: '#64748b' }}>{flight.purpose}</div>}
        <div style={{ color: '#64748b' }}>
          {flightCategoriesService.getById(String(flight.category))?.name ?? `#${flight.category}`} · {flight.planned_altitude_m}m
          {flight.max_distance_m > 0 ? ` · r=${flight.max_distance_m}m` : ''}
        </div>
      </div>
    </Popup>
  );
}

export function FlightsOverlay() {
  const [flights, setFlights] = useState<FlightNotification[]>([]);
  const hiddenIds = useObservable(flightsVisibilityService.hiddenIds$, flightsVisibilityService.hiddenIds$.value);

  useEffect(() => {
    flightCategoriesService.load();
  }, []);

  useEffect(() => {
    const sub = getFlightNotifications().subscribe({ next: setFlights });
    return () => sub.unsubscribe();
  }, []);

  const visibleFlights = flights.filter(f => !hiddenIds.has(f.id));

  return (
    <>
      {visibleFlights.map(flight => {
        const tp = flight.takeoff_point;
        if (!tp) return null;
        const center: [number, number] = [tp.coordinates[1], tp.coordinates[0]];

        return (
          <Marker key={flight.id} position={center} icon={helipagIcon} >
            <FlightPopup flight={flight} extra_text={"Takeoff / Mesto vzleta"} />
            <Tooltip permanent direction="right" className="leaflet-tooltip-small">
              Takeoff / Mesto vzleta
            </Tooltip>
          </Marker>
        );
      })}

      {visibleFlights.flatMap(flight => {
        const points = flight.additional_takeoff_points?.coordinates ?? [];
        return points.map(([lng, lat], i) => (
          <CircleMarker
            key={`extra-tp-${flight.id}-${i}`}
            center={[lat, lng]}
            radius={8}
            pathOptions={{ color: ADDITIONAL_TAKEOFF_COLOR, fillColor: ADDITIONAL_TAKEOFF_COLOR, fillOpacity: 0.8 }}
          >
            <FlightPopup flight={flight} extra_text={`Dodatno vzletišče ${i + 1} / Additional takeoff ${i + 1}`} />
          </CircleMarker>
        ));
      })}

      {visibleFlights.map(flight => {
        const tp = flight.takeoff_point;
        if (!tp) return null;
        const center: [number, number] = [tp.coordinates[1], tp.coordinates[0]];

        // Termination zone (outermost — draw first so it's underneath)
        const termEl = flight.termination_zone ? (
          <Polygon
            key={`term-${flight.id}`}
            positions={toPositions(flight.termination_zone)}
            pathOptions={{ color: TERM_COLOR, fillColor: TERM_COLOR, fillOpacity: 0.2, weight: 1, dashArray: '4' }}
          >
            <FlightPopup flight={flight} extra_text={"Termination zone"}/>

          </Polygon>
        ) : null;

        // Buffer zone
        const bufferEl = flight.buffer_zone ? (
          <Polygon
            key={`buf-${flight.id}`}
            positions={toPositions(flight.buffer_zone)}
            pathOptions={{ color: BUFFER_COLOR, fillColor: BUFFER_COLOR, fillOpacity: 0.5, weight: 1, dashArray: '4' }}

          >            <FlightPopup flight={flight} extra_text={"Buffer zone"}/>

          </Polygon>
        ) : null;

        // Flight area polygon or open-category circle
        let areaEl: React.ReactElement | null = null;
        if (flight.flight_area) {
          areaEl = (
            <Polygon
              key={`area-${flight.id}`}
              positions={toPositions(flight.flight_area)}
              pathOptions={{ color: FLIGHT_COLOR, fillColor: FLIGHT_COLOR, fillOpacity: 0.3, weight: 2 }}
            >
              <FlightPopup flight={flight} extra_text={"Flight area"} />
            </Polygon>
          );
        } else if (flight.max_distance_m > 0) {
          areaEl = (
            <Circle
              key={`area-${flight.id}`}
              center={center}
              radius={flight.max_distance_m}
              pathOptions={{ color: FLIGHT_COLOR, fillColor: FLIGHT_COLOR, fillOpacity: 0.08, weight: 1.5, dashArray: '4 4' }}
            >
              <FlightPopup flight={flight} extra_text={"Flight area"}/>
            </Circle>
          );
        }

        return (
          <React.Fragment key={`zones-${flight.id}`}>
            {termEl}
            {bufferEl}
            {areaEl}
          </React.Fragment>
        );
      })}
    </>
  );
}
